import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useDocumentTitle } from "usehooks-ts";
import { executeQuery } from "../../../utils";

type logType = {
  consumerKey: string;
  createdAt: Date;
  errorCode: string;
  errorType: string;
  email: string;
  user: string;
  resource_link_id: string;
  contextTitle: string;
};

const LtiErrors = () => {
  useDocumentTitle("LTI Logs");

  const [formData, setFormData] = useState({
    email: "",
    integrationKey: "",
    errorCode: "",
  });

  const [queryParams, setQueryParams] = useState<{
    email: string;
    integrationKey: string;
    errorCode: string;
  } | null>(null);

  const { data, isLoading, isError } = useQuery<logType[]>({
    queryKey: [
      "erroredLogs",
      queryParams?.email,
      queryParams?.integrationKey,
      queryParams?.errorCode,
    ],
    refetchOnWindowFocus: false,
    retry: false,
    enabled:
      !!queryParams && (!!queryParams.email || !!queryParams.integrationKey),
    queryFn: () =>
      executeQuery({
        path: "/manager_new/integrations/errored-logs",
        params: {
          email: queryParams?.email || "",
          integrationKey: queryParams?.integrationKey || "",
          errorCode: queryParams?.errorCode || "",
        },
      }),
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold">LTI Error Logs</h1>
      <a
        href="https://docs.google.com/spreadsheets/d/1HFv_VnB3k6bxIZcUbhwEt-3LTciYNMEsoMQZgW4eSNM/edit?gid=0#gid=0"
        target="_blank"
        rel="noopener noreferrer"
        className="mb-10 inline-block text-blue-600 hover:underline"
      >
        Link to error codes and messages
      </a>

      <form className="mb-6 space-y-4">
        <div>
          <label
            htmlFor="email"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Email Address
          </label>
          <input
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter email address"
          />
        </div>

        <div>
          <label
            htmlFor="integrationKey"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Integration Key
          </label>
          <input
            type="text"
            id="integrationKey"
            name="integrationKey"
            value={formData.integrationKey}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter integration key"
          />
        </div>

        <div>
          <label
            htmlFor="errorCode"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Error Code (Optional)
          </label>
          <input
            type="text"
            id="errorCode"
            name="errorCode"
            value={formData.errorCode}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter error code"
          />
        </div>

        <button
          disabled={
            isLoading ||
            (!formData.email && !formData.integrationKey && !formData.errorCode)
          }
          className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          onClick={(e) => {
            e.preventDefault();
            setQueryParams({ ...formData });
          }}
        >
          {isLoading ? "Loading..." : "Submit"}
        </button>
      </form>

      {isError && (
        <div className="mb-4 rounded border border-red-400 bg-red-100 p-4 text-red-700">
          Error
        </div>
      )}

      {data && data.length > 0 && (
        <div>
          <h2 className="mb-4 text-xl font-semibold">
            Error Logs ({data.length} results)
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Created At (Local time)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Deployment ID / Key
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Error Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Error Code
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    User Id
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Resource Link Id
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Context Title
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.map((log, index) => (
                  <tr key={index}>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.email}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.consumerKey}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.errorType}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.errorCode}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.user}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.resource_link_id}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {log.contextTitle}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LtiErrors;
